/**
 * Financial statements — the second publication category.
 *
 * ## Why this is structured and not a PDF
 *
 * A company could send a PDF of its balance sheet and EGBA could publish the
 * picture. But the piece is billed by the centimetre of column, so its size has
 * to be *composed*, not received — and a balance sheet composed from account
 * lines can be set in two columns, with the comparative year beside it, in the
 * gazette's own typography. A PDF can only be scaled, and scaling a document
 * until it fits is how a published statement becomes unreadable.
 *
 * Structure also lets the platform check what a reader would otherwise have to:
 * that assets equal liabilities plus equity, that the totals add up, that the
 * comparative year is actually a different year. A balance sheet published in
 * the official gazette with a broken total is a correction notice waiting to
 * happen, at the company's expense.
 *
 * ## Legal basis
 *
 * Art. 289 of Lei 6.404/76, as amended by Lei 13.818/2019: publicly held
 * corporations publish their financial statements. The platform does not decide
 * who must publish — it composes what is sent, checks it adds up, and prices it.
 *
 * Pure module. No I/O, no clock.
 */

export const TIPOS_DEMONSTRACAO = [
  'balanco_patrimonial',
  'dre',
  'dmpl',
  'dfc',
  'dva',
  'notas_explicativas',
  'parecer_auditoria',
] as const

export type TipoDemonstracao = (typeof TIPOS_DEMONSTRACAO)[number]

export const ROTULOS_DEMONSTRACAO: Record<TipoDemonstracao, { nome: string; sigla: string }> = {
  balanco_patrimonial: { nome: 'Balanço Patrimonial', sigla: 'BP' },
  dre: { nome: 'Demonstração do Resultado do Exercício', sigla: 'DRE' },
  dmpl: { nome: 'Demonstração das Mutações do Patrimônio Líquido', sigla: 'DMPL' },
  dfc: { nome: 'Demonstração dos Fluxos de Caixa', sigla: 'DFC' },
  dva: { nome: 'Demonstração do Valor Adicionado', sigla: 'DVA' },
  notas_explicativas: { nome: 'Notas Explicativas', sigla: 'NE' },
  parecer_auditoria: { nome: 'Parecer dos Auditores Independentes', sigla: 'Parecer' },
}

/**
 * One line of a statement.
 *
 * `nivel` drives indentation in the composed piece; `grupo` marks a subtotal
 * line, which is set in bold and separated by a rule. Values are in **integer
 * centavos** for the same reason prices are: a balance sheet that fails to
 * balance by one centavo because of floating point is a balance sheet nobody
 * can defend.
 */
export interface LinhaConta {
  /** Conta or description, exactly as it will be printed. */
  descricao: string
  /** Nesting level, 0-based. Deeper lines are indented. */
  nivel: number
  /** Value for the reference year, in centavos. Null for a heading line. */
  valorCentavos: number | null
  /** Value for the comparative year, in centavos. */
  comparativoCentavos?: number | null
  /** Subtotal or total: printed in bold, above a rule. */
  grupo?: boolean
  /**
   * Which side of the balance sheet this line belongs to.
   * Only meaningful in `balanco_patrimonial`.
   */
  lado?: 'ativo' | 'passivo'
}

export interface Demonstracao {
  tipo: TipoDemonstracao
  exercicio: number
  exercicioComparativo?: number | null
  /** Free text printed before the table — used by notes and the audit opinion. */
  texto?: string
  linhas: LinhaConta[]
}

export interface Cabecalho {
  razaoSocial: string
  cnpj: string
  /** e.g. "Exercícios findos em 31 de dezembro de 2026 e 2025". */
  subtitulo?: string
}

// ---------------------------------------------------------------------------
// Validação
// ---------------------------------------------------------------------------

export type GravidadeConta = 'rejeita' | 'avisa'

export interface ProblemaDemonstracao {
  demonstracao: TipoDemonstracao
  linha: number | null
  codigo: CodigoProblemaDemonstracao
  mensagem: string
  gravidade: GravidadeConta
}

export type CodigoProblemaDemonstracao =
  | 'sem_linhas'
  | 'descricao_vazia'
  | 'exercicio_invalido'
  | 'comparativo_igual'
  | 'balanco_nao_fecha'
  | 'sem_total_ativo'
  | 'sem_total_passivo'
  | 'nivel_invalido'
  | 'sem_valor'
  | 'tipo_duplicado'

const ANO_MINIMO = 1976 // a própria Lei 6.404

function limpar(v: unknown): string {
  return String(v ?? '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Sums the top-level `grupo` lines of one side of the balance sheet. */
function totalDoLado(linhas: readonly LinhaConta[], lado: 'ativo' | 'passivo'): number | null {
  const doLado = linhas.filter((l) => l.lado === lado)
  if (doLado.length === 0) return null

  // O total é a linha de grupo mais rasa: "Total do Ativo" está no nível 0,
  // "Ativo Circulante" no 1. Somar tudo contaria o mesmo dinheiro duas vezes.
  const nivelMinimo = Math.min(...doLado.filter((l) => l.grupo).map((l) => l.nivel))
  const totais = doLado.filter((l) => l.grupo && l.nivel === nivelMinimo)
  if (totais.length === 0) return null

  return totais.reduce((s, l) => s + (l.valorCentavos ?? 0), 0)
}

/**
 * Validates one statement.
 *
 * The balance check is the one that earns its place: a balance sheet that does
 * not balance is wrong in a way the company will be asked about, and finding it
 * before publication costs nothing while finding it after costs a correction
 * notice.
 */
export function validarDemonstracao(d: Demonstracao): ProblemaDemonstracao[] {
  const problemas: ProblemaDemonstracao[] = []
  const push = (
    codigo: CodigoProblemaDemonstracao,
    mensagem: string,
    gravidade: GravidadeConta = 'rejeita',
    linha: number | null = null,
  ) => problemas.push({ demonstracao: d.tipo, linha, codigo, mensagem, gravidade })

  const anoAtual = ANO_MINIMO + 200
  if (!Number.isInteger(d.exercicio) || d.exercicio < ANO_MINIMO || d.exercicio > anoAtual) {
    push('exercicio_invalido', `Exercício ${d.exercicio} não parece um ano válido.`)
  }

  if (d.exercicioComparativo != null && d.exercicioComparativo === d.exercicio) {
    push('comparativo_igual', 'O exercício comparativo tem de ser diferente do exercício.')
  }

  // Texto puro (notas, parecer) não precisa de linhas de conta.
  const somenteTexto = d.tipo === 'notas_explicativas' || d.tipo === 'parecer_auditoria'
  if (d.linhas.length === 0 && !(somenteTexto && limpar(d.texto))) {
    push('sem_linhas', 'A demonstração está vazia.')
    return problemas
  }

  d.linhas.forEach((l, i) => {
    const numero = i + 1
    if (!limpar(l.descricao)) {
      push('descricao_vazia', 'Linha sem descrição.', 'rejeita', numero)
    }
    if (!Number.isInteger(l.nivel) || l.nivel < 0 || l.nivel > 5) {
      push('nivel_invalido', `Nível ${l.nivel} fora da faixa (0 a 5).`, 'rejeita', numero)
    }
    // Linha de cabeçalho sem valor é legítima; linha de grupo sem valor não.
    if (l.grupo && l.valorCentavos == null) {
      push('sem_valor', `"${limpar(l.descricao)}" é um total e está sem valor.`, 'rejeita', numero)
    }
  })

  if (d.tipo === 'balanco_patrimonial') {
    const ativo = totalDoLado(d.linhas, 'ativo')
    const passivo = totalDoLado(d.linhas, 'passivo')

    if (ativo === null) push('sem_total_ativo', 'Falta o total do ativo.')
    if (passivo === null) push('sem_total_passivo', 'Falta o total do passivo e patrimônio líquido.')

    if (ativo !== null && passivo !== null && ativo !== passivo) {
      const diferenca = Math.abs(ativo - passivo)
      push(
        'balanco_nao_fecha',
        `O ativo (${formatarCentavos(ativo)}) não fecha com o passivo mais o patrimônio ` +
          `líquido (${formatarCentavos(passivo)}). Diferença de ${formatarCentavos(diferenca)}.`,
      )
    }
  }

  return problemas
}

/** Validates the whole set that will be published together. */
export function validarConjunto(demonstracoes: readonly Demonstracao[]): ProblemaDemonstracao[] {
  const problemas = demonstracoes.flatMap((d) => validarDemonstracao(d))

  const vistos = new Map<string, number>()
  for (const d of demonstracoes) {
    const chave = `${d.tipo}|${d.exercicio}`
    if (vistos.has(chave)) {
      problemas.push({
        demonstracao: d.tipo,
        linha: null,
        codigo: 'tipo_duplicado',
        mensagem: `${ROTULOS_DEMONSTRACAO[d.tipo].nome} de ${d.exercicio} aparece duas vezes.`,
        gravidade: 'rejeita',
      })
    }
    vistos.set(chave, (vistos.get(chave) ?? 0) + 1)
  }

  return problemas
}

export function conjuntoValido(problemas: readonly ProblemaDemonstracao[]): boolean {
  return problemas.every((p) => p.gravidade !== 'rejeita')
}

// ---------------------------------------------------------------------------
// Formatação e medida
// ---------------------------------------------------------------------------

/** Brazilian accounting format: thousands separator, two decimals, () for negatives. */
export function formatarCentavos(centavos: number | null | undefined): string {
  if (centavos == null) return ''
  const negativo = centavos < 0
  const inteiro = Math.abs(centavos)
  const texto = (inteiro / 100)
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return negativo ? `(${texto})` : texto
}

/**
 * Estimates the composed height, in centimetres of column.
 *
 * This is what makes a *quote* possible before anything is typeset — the whole
 * reason the statement is structured. It is an estimate and is labelled as one
 * everywhere it appears: the price that is charged comes from the real measured
 * piece, exactly as with the concluintes list.
 *
 * The constants come from the gazette's own layout: roughly 22 lines of body
 * text per 10 cm of column at the size used for tables.
 */
export const LINHAS_POR_CM = 2.2

export interface EstimativaComposicao {
  linhasTotais: number
  /** Height in cm for a single column. */
  alturaCm: number
  /** Suggested column count — statements read badly across more than two. */
  colunas: number
  /** Height per column once split. */
  alturaPorColunaCm: number
}

export function estimarComposicao(
  demonstracoes: readonly Demonstracao[],
  colunas = 2,
): EstimativaComposicao {
  const linhasTotais = demonstracoes.reduce((soma, d) => {
    // Cabeçalho da demonstração + linhas + respiro entre blocos.
    const doTexto = d.texto ? Math.ceil(limpar(d.texto).length / 90) : 0
    return soma + 3 + d.linhas.length + doTexto
  }, 0)

  const alturaCm = Math.ceil(linhasTotais / LINHAS_POR_CM)
  const c = Math.max(1, Math.min(colunas, 3))

  return {
    linhasTotais,
    alturaCm,
    colunas: c,
    alturaPorColunaCm: Math.ceil(alturaCm / c),
  }
}
